import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageAction } from '../models';
import { D2CUser } from '../providers/d2c-user';

import { AuthService } from './auth.service';

@Injectable()
export class RoleGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private user: D2CUser
  ) {}

  private static getPages(
    state: RouterStateSnapshot,
    typeOfPage: string
  ): string[] {
    const urlSegments = state.url.split('/');
    const paths = [];
    for (const urlSegment of urlSegments) {
      if (
        urlSegment !== null &&
        urlSegment.length !== 0 &&
        urlSegment !== 'app'
      ) {
        if (urlSegment.indexOf(typeOfPage) !== -1) {
          break;
        }

        paths.push(urlSegment);
      }
    }

    return paths;
  }

  canEnter(route, state, typeOfPage, action: PageAction) {
    if (state.url.indexOf(typeOfPage) === -1) {
      return true;
    }

    const pagesToCheck = RoleGuard.getPages(state, typeOfPage);
    let canEnter = true;
    if (action === PageAction.allow) {
      pagesToCheck.forEach((page) => {
        canEnter = canEnter && this.authService.doesActionExist(page, action);
      });
    } else {
      // For the specific action we just want the last part of the tree because it represents the object to modify
      const page = pagesToCheck[pagesToCheck.length - 1];
      canEnter = canEnter && this.authService.doesActionExist(page, action);
    }

    return canEnter;
  }

  canEnterCentral() {
    return this.authService.doesActionExist('d2c-central', PageAction.allow);
  }

  canVendorEnterCentral() {
    try {
      return (
        (this.user && this.user.isTelusMember()) ||
        (this.user &&
          !this.user.isTelusMember() &&
          this.user.vendor &&
          this.user.vendor.isActive)
      );
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  // Checks detail pages AKA allow PageAction
  canEnterDetail(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canEnter(route, state, 'detail', PageAction.allow);
  }

  // Checks new pages AKA post PageAction
  canEnterNew(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canEnter(route, state, 'new', PageAction.post);
  }

  // Checks edit pages AKA put PageAction
  canEnterEdit(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canEnter(route, state, 'new', PageAction.put);
  }

  checkRole(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return (
      this.canEnterDetail(route, state) &&
      this.canEnterNew(route, state) &&
      this.canEnterEdit(route, state)
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.authService.canActivateProtectedRoutes$.pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          return false;
        }
        const canEnterCentral =
          this.canEnterCentral() && this.canVendorEnterCentral();
        if (!canEnterCentral) {
          this.authService.navigateToLoginPage(true, 'unauthorized');
          return false;
        }
        const canContinue = this.checkRole(route, state);
        if (!canContinue) {
          sessionStorage.setItem(
            'restrictedPage',
            JSON.stringify(this.router.routerState.snapshot.url)
          );
          this.router.navigateByUrl('/app/menu');
          return false;
        }
        return canContinue;
      })
    );
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.canActivate(childRoute, state);
  }
}
